import { useEffect, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { Archive, Bot, FileText, ListChecks, Users } from "lucide-react";
import { WorkshopNavigator } from "@/components/shell/WorkshopNavigator";
import { WORKSHOPS } from "@/content/workshops";
import { SmokeCanvas } from "@/components/fx/SmokeCanvas";
import { BriefPanel } from "./BriefPanel";
import { briefByWorkshop } from "./content";
import { resolveBrief } from "./overrides/workshop-override-content";
import { useEffectiveWorkshopOverride } from "@/stores/useEffectiveWorkshopOverride";
import { useWorkshopContentDefaults } from "@/stores/useWorkshopContentDefaults";
import { useWorkshopOverrides } from "@/stores/useWorkshopOverrides";
import { HawPanel } from "./HawPanel";
import { WorkshopThemeDiagram } from "./WorkshopThemeDiagram";
import { ModalTriggers } from "./modals/ModalTriggers";
import { XaesAnalyst } from "@/features/xaes-analyst/XaesAnalyst";
import { RunSequence } from "@/features/run-sequence/RunSequence";
import { AssetLocker } from "@/features/asset-locker/AssetLocker";
import { useXaesAgentModal } from "@/stores/useXaesAgentModal";

/**
 * Phase 2 (1/N) — Workshop Dashboard route shell. Ported from the
 * top-nav + dashboard grid in `_legacy/wd.html`.
 *
 * Slice 1 includes:
 *   - URL-param-driven workshop resolution (invalid id → redirect to
 *     MissionControl).
 *   - Workshop name + status header with a Back-to-Mission-Control
 *     affordance.
 *   - Brief panel (intro video / pre-reading / questionnaire).
 *   - Sticky-bottom WorkshopNavigator that lets facilitators jump
 *     between workshop dashboards.
 *
 * Pending slices: HAW bubble panel + crew/team rosters (2/N), agenda
 * + map + modal scaffolding (3/N). The XAES Analyst chat docks into
 * the right column once Phase 4 lands.
 */

type MobilePane = "brief" | "sequence" | "locker" | "model" | "analyst";

const MOBILE_TABS: { key: MobilePane; label: string; icon: typeof FileText }[] = [
  { key: "brief", label: "Brief", icon: FileText },
  { key: "sequence", label: "Sequence", icon: ListChecks },
  { key: "locker", label: "Locker", icon: Archive },
  { key: "model", label: "Model", icon: Users },
  { key: "analyst", label: "Analyst", icon: Bot },
];

export function WorkshopDashboard() {
  const { id } = useParams();
  const workshop = WORKSHOPS.find((w) => w.id === id);
  const isAgentModalOpen = useXaesAgentModal((s) => s.isOpen);
  // Phone-tier: one panel full-height at a time, switched via an icon
  // row, instead of stacking all five and scrolling through them (UAT:
  // Asset Locker uploads buried three scrolls down under Brief + Run
  // Sequence). Desktop keeps the original 3x2 console grid unconditionally
  // — this only matters below `lg`. Brief opens first: it's the workshop's
  // own entry point (intro video / pre-reading), the natural first stop.
  const [mobilePane, setMobilePane] = useState<MobilePane>("brief");
  // Power-user content overrides for this workshop (BFF-shared, IDB-cached).
  // Hydrate once on mount; `useEffectiveWorkshopOverride` re-renders when it lands.
  // Called before the early return so hook order stays stable.
  const override = useEffectiveWorkshopOverride(id ?? "");
  useEffect(() => {
    void useWorkshopOverrides.getState().hydrate();
    void useWorkshopContentDefaults.getState().hydrate();
  }, []);
  // Remember the workshop for surfaces opened without one — the
  // Repository overlay defaults its records bucket to this instead of
  // silently filing everything under WS1 (UAT: transcripts "vanished").
  useEffect(() => {
    if (workshop) sessionStorage.setItem("xaes:last-workshop", workshop.id);
  }, [workshop]);

  if (!workshop) {
    return <Navigate to="/" replace />;
  }

  // With no override set, `resolveBrief` returns the authored rows unchanged.
  const briefItems = resolveBrief(override, briefByWorkshop[workshop.id] ?? []);

  return (
    <div className="relative isolate flex h-full flex-col overflow-hidden">
      {/*
        Solid dark backdrop + drifting smoke. Matches the legacy
        `_legacy/wd.html` body treatment: a solid `var(--bg-deep)`
        wash with `#wd-bg-smoke` painted over it. There is no hero
        photo on the legacy workshop dashboard — only Mission Control
        gets the operations-room image. The smoke field below provides
        the visual motion behind every grid cell.
      */}
      <div
        aria-hidden
        className="absolute inset-0 -z-10"
        style={{ background: "var(--bg-deep)" }}
      />
      {/* Page-wide drifting dot smoke. Ports `#wd-bg-smoke` from
          `_legacy/wd.html` line 30 — a single canvas sized to the full
          dashboard viewport that paints behind every grid cell. The
          per-panel SmokeCanvas instances (HAW / WorkshopPanel) remain
          for accent-tinted local emphasis; this one is the page wash
          that the legacy used so even empty cells read as "alive". */}
      <SmokeCanvas
        className="pointer-events-none absolute inset-0 -z-10 h-full w-full"
        cssVarName="--accent-true-blue"
        ariaLabel="Workshop dashboard ambient smoke field"
      />

      {/*
        Dashboard grid mirrors `_legacy/wd.html` lines 33-63 and the
        `.dashboard` rule in `_legacy/xaes.css` lines 1331-1340:
        a 3×2 even grid with explicit placement that fills the viewport
        between HudHeader (56px) and WorkshopNavigator (~112px).

          col 1 row 1   — Brief
          col 2 row 1   — Run Sequence
          col 1 row 2   — Asset Locker
          col 2 row 2   — HAW
          col 3 rows 1-2 — XAES Analyst (spans both rows)

        Crew lives only in the HudHeader → Meet Crew modal (legacy has
        no Crew dashboard slot); the React port previously stacked
        CrewPanel under AssetLocker as a Loveable-era extra, now
        removed so AssetLocker stretches the full row-2 cell.

        Legacy used `gap: 12px; padding: 12px;` (xaes.css 1331-1340);
        we match with `gap-3` / `p-3` so the grid claims the available
        height. Each cell is `min-h-0 overflow-hidden` and child panels
        use `flex-1 min-h-0` so per-panel bodies scroll internally
        instead of the whole section.

        HudHeader carries the status pill / session timer / Mission
        Control back affordance, so this view jumps straight from the
        topnav (rendered by HudHeader) to the grid, matching legacy.

        ModalTriggers is headless — it only mounts modal dialogs that
        BriefPanel rows open via the shared store — so its placement
        doesn't affect layout. It sits with BriefPanel in col 1.
      */}
      <section className="panel-glow-ws flex w-full min-h-0 flex-1 flex-col overflow-hidden p-2">
        {/* Phone-tier pane switch — an icon row, not text tabs
            (`.mc-mobile-tab`'s pattern doesn't scale to five
            destinations at phone width). Every cell below stays
            mounted; only the active one is visible, so switching back
            to Asset Locker doesn't lose an in-progress upload or reset
            the analyst's scroll position. `lg:hidden` means desktop's
            3x2 grid is completely unaffected. */}
        <div
          className="mc-mobile-tabs flex flex-shrink-0 gap-1.5 pb-2 lg:hidden"
          role="tablist"
          aria-label="Workshop dashboard view"
        >
          {MOBILE_TABS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={mobilePane === key}
              onClick={() => setMobilePane(key)}
              className={`wd-mobile-tab ${mobilePane === key ? "wd-mobile-tab-active" : ""}`}
            >
              <Icon aria-hidden style={{ width: 16, height: 16 }} strokeWidth={2} />
              <span className="wd-mobile-tab-label">{label}</span>
            </button>
          ))}
        </div>

        {/* Below lg only the active pane renders (full height, no
            scroll-through-five-panels); at lg+ this reverts to the
            viewport-locked 3x2 console grid, unchanged. */}
        <div className="grid min-h-0 flex-1 gap-2 lg:grid-cols-3 lg:grid-rows-2">
          {/* col 1 row 1 — Brief. ModalTriggers is headless (Dialog
              portals) so it sits outside the flex-fill wrapper to
              avoid claiming grid space. */}
          <div
            className={`min-h-0 min-w-0 flex-col overflow-hidden lg:col-start-1 lg:row-start-1 lg:flex ${
              mobilePane === "brief" ? "flex" : "hidden"
            }`}
          >
            <div className="flex min-h-0 flex-1 flex-col [&>*]:min-h-0 [&>*]:flex-1">
              <BriefPanel items={briefItems} workshopId={workshop.id} />
            </div>
            <ModalTriggers workshopId={workshop.id} />
          </div>

          {/* col 2 row 1 — Run Sequence */}
          <div
            className={`min-h-0 min-w-0 flex-col overflow-hidden lg:col-start-2 lg:row-start-1 lg:flex [&>*]:min-h-0 [&>*]:flex-1 ${
              mobilePane === "sequence" ? "flex" : "hidden"
            }`}
          >
            <RunSequence workshopId={workshop.id} />
          </div>

          {/* col 1 row 2 — Asset Locker (Crew dropped — modal-only) */}
          <div
            className={`min-h-0 min-w-0 flex-col overflow-hidden lg:col-start-1 lg:row-start-2 lg:flex [&>*]:min-h-0 [&>*]:flex-1 ${
              mobilePane === "locker" ? "flex" : "hidden"
            }`}
          >
            <AssetLocker workshopId={workshop.id} />
          </div>

          {/* col 2 row 2 — workshop theme diagram.
              WS4 keeps the specialised Human-Agent Ways of Working
              modes panel; every other workshop renders its own
              Mission Control bubble graphic so the slot is not
              identical across the program. */}
          <div
            className={`min-h-0 min-w-0 flex-col overflow-hidden lg:col-start-2 lg:row-start-2 lg:flex [&>*]:min-h-0 [&>*]:flex-1 ${
              mobilePane === "model" ? "flex" : "hidden"
            }`}
          >
            {workshop.id === "WS4" ? (
              <HawPanel />
            ) : (
              <WorkshopThemeDiagram workshopId={workshop.id} />
            )}
          </div>

          {/* col 3 rows 1-2 — XAES Analyst sidebar (gated while the
              floating agent modal is open so we don't double-render).
              Below lg it's a normal tab pane like the other four
              instead of joining the stack — the icon row is now the
              route to the analyst on narrow screens, so it no longer
              needs to always be present. */}
          {!isAgentModalOpen && (
            <aside
              className={`min-h-0 min-w-0 overflow-hidden lg:col-start-3 lg:row-span-2 lg:row-start-1 lg:flex ${
                mobilePane === "analyst" ? "flex" : "hidden"
              }`}
            >
              <XaesAnalyst workshopId={workshop.id} className="min-h-0 flex-1" />
            </aside>
          )}
        </div>
        {/* HandshakeBoard + StressTest are mounted as modal dialogs in
            ModalTriggers (headless host) and launched from the matching
            Activity 2 objective rows inside RunSequence. */}
      </section>

      <WorkshopNavigator />
    </div>
  );
}
